import * as d3 from 'd3';
import { OrbitConfig, NodeData, CENTER_X, CENTER_Y, NODE_RADIUS, LABEL_OFFSET, MIN_SETTLEMENT_GAP_RAD, DECELERATION_DURATION, ORBIT_STAGGER_ANGLE } from './constants';
import { renderOrbitalNode, renderSatellites, getSatelliteCount, calculateOrbitalNodeSize } from './nodeRenderer';

/**
 * RAF throttle interval - limit updates to ~60fps to prevent wasted CPU cycles.
 * D3 can attempt to update faster than the browser can repaint, so we throttle to match browser refresh rate.
 */
const RAF_THROTTLE_MS = 16;

/**
 * Update label position based on node's current x position.
 * When node is on the left side, flip label to the left to avoid dial overlap.
 * Uses hysteresis to prevent rapid toggling near the center.
 *
 * @param nodeElement - The node's DOM element
 * @param x - Current x position of the node
 * @param labelWidth - Width of the label (stored as data attribute)
 */
export function updateLabelPosition(nodeElement: SVGGElement, x: number, labelWidth: number): void {
  const labelGroup = d3.select(nodeElement).select('.custom-label-group');
  if (labelGroup.empty()) return;

  const isCurrentlyLeft = labelGroup.classed('label-left');

  // Hysteresis buffer to prevent rapid toggling at top/bottom
  const HYSTERESIS = 50;

  // When node is on left side of center, label extends toward dial - flip to left
  // When node is on right side, label extends away from dial - keep on right
  let shouldFlipLeft: boolean;

  if (isCurrentlyLeft) {
    // Currently on left - only flip back to right when well past center
    shouldFlipLeft = x < CENTER_X + HYSTERESIS;
  } else {
    // Currently on right - only flip to left when well past center
    shouldFlipLeft = x < CENTER_X - HYSTERESIS;
  }

  if (shouldFlipLeft && !isCurrentlyLeft) {
    // Flip to left side
    labelGroup.classed('label-left', true);

    const rect = labelGroup.select('.label-background');
    const text = labelGroup.select('.custom-label-text');

    // Move rect to left side (negative x position)
    rect
      .transition()
      .duration(600)
      .ease(d3.easeQuadInOut)
      .attr('x', -(LABEL_OFFSET + labelWidth));

    // Move text to left side
    text
      .transition()
      .duration(600)
      .ease(d3.easeQuadInOut)
      .attr('x', -(LABEL_OFFSET + labelWidth / 2));

  } else if (!shouldFlipLeft && isCurrentlyLeft) {
    // Flip back to right side
    labelGroup.classed('label-left', false);

    const rect = labelGroup.select('.label-background');
    const text = labelGroup.select('.custom-label-text');

    // Move rect back to right side
    rect
      .transition()
      .duration(600)
      .ease(d3.easeQuadInOut)
      .attr('x', LABEL_OFFSET);

    // Move text back to right side
    text
      .transition()
      .duration(600)
      .ease(d3.easeQuadInOut)
      .attr('x', LABEL_OFFSET + labelWidth / 2);
  }
}

/**
 * Calculate animation speed based on velocity.
 * Higher absolute velocity = faster rotation (more attention needed).
 *
 * @param baseSpeed - Base speed in ms for full rotation from OrbitConfig
 * @param velocity - Velocity score from -100 to +100
 * @returns Adjusted speed in ms (lower = faster rotation)
 */
export function calculateOrbitSpeed(baseSpeed: number, velocity: number): number {
  const absVelocity = Math.abs(velocity);
  // Exponential curve: low velocity barely changes speed, high velocity dramatically increases it
  // velocity 0   → multiplier 1.0  (base speed)
  // velocity 25  → multiplier ~0.72 (~1.4x faster)
  // velocity 50  → multiplier ~0.42 (~2.4x faster)
  // velocity 75  → multiplier ~0.18 (~5.6x faster)
  // velocity 100 → multiplier 0.05  (20x faster)
  const t = absVelocity / 100;
  const multiplier = Math.pow(1 - t, 2.5) * 0.95 + 0.05;
  return Math.max(baseSpeed * multiplier, 800);
}

/**
 * Calculate average velocity for an array of nodes.
 * Used when all nodes in an orbit should rotate at a unified speed.
 *
 * @param nodes - Array of NodeData with optional velocity
 * @returns Average absolute velocity (0 if no nodes)
 */
export function calculateAverageVelocity(nodes: NodeData[]): number {
  if (nodes.length === 0) return 0;
  const totalAbsVelocity = nodes.reduce(
    (sum, node) => sum + Math.abs(node.velocity ?? 0),
    0
  );
  return totalAbsVelocity / nodes.length;
}

/**
 * Calculate resting angles for nodes after settlement.
 * Nodes are sorted by gravity (highest = 12 o'clock) and distributed clockwise
 * with size-aware spacing to prevent overlap.
 *
 * @param nodes - Array of NodeData for a single orbit
 * @param orbitRadius - Radius of the orbit in pixels
 * @returns Map from original node index to target angle (radians)
 */
export function calculateSettlementAngles(nodes: NodeData[], orbitRadius: number, orbitIndex = 0): Map<number, number> {
  const result = new Map<number, number>();

  if (nodes.length === 0) return result;

  // Golden angle stagger per orbit to maximize angular separation between adjacent orbits
  const staggerOffset = orbitIndex * ORBIT_STAGGER_ANGLE;

  // Single node goes to its staggered 12 o'clock position
  if (nodes.length === 1) {
    result.set(0, -Math.PI / 2 + staggerOffset);
    return result;
  }

  // Stable sort by gravity descending, using original index as tiebreaker
  const indexed = nodes.map((node, i) => ({ node, originalIndex: i }));
  indexed.sort((a, b) => {
    const gravityDiff = (b.node.gravity ?? 0) - (a.node.gravity ?? 0);
    if (gravityDiff !== 0) return gravityDiff;
    return a.originalIndex - b.originalIndex;
  });

  // Calculate the angular footprint each node occupies on this orbit
  const nodeRadii = indexed.map(({ node }) => {
    const impact = node.impact ?? 50;
    return calculateOrbitalNodeSize(impact) / 2;
  });

  // Calculate minimum gap between each adjacent pair (including wrap-around)
  const minGaps: number[] = [];
  for (let i = 0; i < indexed.length; i++) {
    const nextIdx = (i + 1) % indexed.length;
    const gap = Math.asin(Math.min(nodeRadii[i] / orbitRadius, 1))
              + MIN_SETTLEMENT_GAP_RAD
              + Math.asin(Math.min(nodeRadii[nextIdx] / orbitRadius, 1));
    minGaps.push(gap);
  }

  const totalMinSpan = minGaps.reduce((sum, g) => sum + g, 0);
  const fullCircle = 2 * Math.PI;

  // Distribute remaining space evenly across all gaps
  const extraSpace = Math.max(0, fullCircle - totalMinSpan);
  const extraPerGap = extraSpace / indexed.length;

  // Compute cumulative angles: highest gravity at 12 o'clock, rest clockwise
  const compressionRatio = totalMinSpan > fullCircle ? fullCircle / totalMinSpan : 1;
  const angles: number[] = [0];
  for (let i = 1; i < indexed.length; i++) {
    const prevAngle = angles[i - 1];
    const gap = minGaps[i - 1] * compressionRatio + (compressionRatio < 1 ? 0 : extraPerGap);
    angles.push(prevAngle + gap);
  }

  for (let i = 0; i < indexed.length; i++) {
    const targetAngle = -Math.PI / 2 + staggerOffset + angles[i];
    result.set(indexed[i].originalIndex, targetAngle);
  }

  return result;
}

/**
 * Calculate size-aware initial angles for nodes on an orbit.
 * Unlike calculateSettlementAngles, this preserves original array order
 * and adds an orbit-level stagger offset so adjacent orbits don't align.
 *
 * @param nodes - Array of NodeData for a single orbit (order preserved)
 * @param orbitRadius - Radius of the orbit in pixels
 * @param orbitIndex - Index of this orbit (used for stagger offset)
 * @returns Map from node index to initial angle (radians)
 */
export function calculateInitialSpacedAngles(
  nodes: NodeData[],
  orbitRadius: number,
  orbitIndex: number
): Map<number, number> {
  const result = new Map<number, number>();

  if (nodes.length === 0) return result;

  const staggerOffset = orbitIndex * ORBIT_STAGGER_ANGLE;
  const startAngle = -Math.PI / 2 + staggerOffset;

  if (nodes.length === 1) {
    result.set(0, startAngle);
    return result;
  }

  // Calculate angular footprint each node needs on this orbit
  const nodeRadii = nodes.map((node) => {
    const impact = node.impact ?? 50;
    return calculateOrbitalNodeSize(impact) / 2;
  });

  // Sum the minimum angular space needed between consecutive nodes
  let totalMinSpan = 0;
  for (let i = 0; i < nodes.length; i++) {
    const curr = nodeRadii[i];
    const next = nodeRadii[(i + 1) % nodes.length];
    totalMinSpan += Math.asin(Math.min(curr / orbitRadius, 1))
                  + MIN_SETTLEMENT_GAP_RAD
                  + Math.asin(Math.min(next / orbitRadius, 1));
  }

  // Distribute remaining space evenly as padding between nodes
  const remainingSpace = Math.max(0, 2 * Math.PI - totalMinSpan);
  const paddingPerGap = remainingSpace / nodes.length;

  let currentAngle = startAngle;
  for (let i = 0; i < nodes.length; i++) {
    result.set(i, currentAngle);

    const curr = nodeRadii[i];
    const next = nodeRadii[(i + 1) % nodes.length];
    const minGap = Math.asin(Math.min(curr / orbitRadius, 1))
                 + MIN_SETTLEMENT_GAP_RAD
                 + Math.asin(Math.min(next / orbitRadius, 1));
    currentAngle += minGap + paddingPerGap;
  }

  // If nodes don't fit, compress proportionally (same approach as settlement)
  if (totalMinSpan > 2 * Math.PI) {
    const compressionRatio = (2 * Math.PI) / totalMinSpan;
    let compressedAngle = startAngle;
    for (let i = 0; i < nodes.length; i++) {
      result.set(i, compressedAngle);

      const curr = nodeRadii[i];
      const next = nodeRadii[(i + 1) % nodes.length];
      const minGap = Math.asin(Math.min(curr / orbitRadius, 1))
                   + MIN_SETTLEMENT_GAP_RAD
                   + Math.asin(Math.min(next / orbitRadius, 1));
      compressedAngle += minGap * compressionRatio;
    }
  }

  return result;
}

/**
 * Start continuous rotation animation for an orbital gradient.
 * Creates a loader/shimmer effect by rotating the gradient around the center point.
 *
 * @param gradient - D3 selection for the linearGradient element
 * @param duration - Duration for one complete rotation in milliseconds
 * @param centerX - X coordinate of rotation center
 * @param centerY - Y coordinate of rotation center
 */
function startGradientRotation(
  gradient: d3.Selection<SVGLinearGradientElement, unknown, null, undefined>,
  duration: number,
  centerX: number,
  centerY: number
): void {
  function animate() {
    gradient
      .transition()
      .duration(duration)
      .ease(d3.easeLinear)
      .attrTween('gradientTransform', () => {
        return (t: number) => {
          const angle = t * 360;
          return `rotate(${angle} ${centerX} ${centerY})`;
        };
      })
      .on('end', animate); // Loop infinitely
  }

  animate();
}

export function createOrbitRings(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  orbits: OrbitConfig[],
  centerImagePath?: string,
  onOrbitClick?: (orbitIndex: number) => void
): d3.Selection<SVGCircleElement, OrbitConfig, SVGGElement, unknown> {
  const orbitGroup = svg.append('g').attr('class', 'orbits');

  // Create gradients for multi-color orbits
  const defs = svg.select('defs').empty() ? svg.append('defs') : svg.select('defs');

  // Create linear gradient for label backgrounds (158.49deg angle)
  const labelGradient = defs
    .append('linearGradient')
    .attr('id', 'labelGradient')
    .attr('x1', '0%')
    .attr('y1', '80%')
    .attr('x2', '100%')
    .attr('y2', '20%');

  labelGradient.append('stop')
    .attr('offset', '13.43%')
    .attr('stop-color', 'rgba(16, 182, 254, 0.5)');

  labelGradient.append('stop')
    .attr('offset', '52.78%')
    .attr('stop-color', 'rgba(53, 110, 164, 0.5)');

  labelGradient.append('stop')
    .attr('offset', '100%')
    .attr('stop-color', 'rgba(30, 186, 254, 0.5)');

  // Create drop shadow filter for label backgrounds
  const labelShadow = defs
    .append('filter')
    .attr('id', 'labelShadow')
    .attr('x', '-20%')
    .attr('y', '-20%')
    .attr('width', '140%')
    .attr('height', '140%');

  labelShadow.append('feDropShadow')
    .attr('dx', 2)
    .attr('dy', 2)
    .attr('stdDeviation', 1)
    .attr('flood-color', 'rgba(38, 168, 223, 0.5)');

  // Create glow filter for nodes
  const nodeGlowFilter = defs
    .append('filter')
    .attr('id', 'node-glow')
    .attr('x', '-100%')
    .attr('y', '-100%')
    .attr('width', '300%')
    .attr('height', '300%');

  nodeGlowFilter.append('feGaussianBlur')
    .attr('in', 'SourceGraphic')
    .attr('stdDeviation', '3')
    .attr('result', 'blur');

  nodeGlowFilter.append('feColorMatrix')
    .attr('in', 'blur')
    .attr('mode', 'matrix')
    .attr('values', '0 0 0 0 0.062 0 0 0 0 0.647 0 0 0 0 0.996 0 0 0 0.8 0')
    .attr('result', 'coloredBlur');

  const nodeMerge = nodeGlowFilter.append('feMerge');
  nodeMerge.append('feMergeNode').attr('in', 'coloredBlur');
  nodeMerge.append('feMergeNode').attr('in', 'SourceGraphic');

  // Create subtle glow filter for orbital rings
  const ringGlowFilter = defs
    .append('filter')
    .attr('id', 'ring-glow')
    .attr('x', '-50%')
    .attr('y', '-50%')
    .attr('width', '200%')
    .attr('height', '200%');

  ringGlowFilter.append('feGaussianBlur')
    .attr('in', 'SourceGraphic')
    .attr('stdDeviation', '1.5')
    .attr('result', 'blur');

  const ringMerge = ringGlowFilter.append('feMerge');
  ringMerge.append('feMergeNode').attr('in', 'blur');
  ringMerge.append('feMergeNode').attr('in', 'SourceGraphic');

  // Create image pattern for 172-radius orbit (if image path provided) - scaled 1.15x
  if (centerImagePath) {
    const orbitRadius = 172;
    const patternSize = orbitRadius * 2 + 20; // Add extra padding to prevent gaps
    const imageSize = patternSize;
    const patternX = CENTER_X - patternSize / 2;
    const patternY = CENTER_Y - patternSize / 2;

    defs
      .append('pattern')
      .attr('id', 'background-image')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('x', patternX)
      .attr('y', patternY)
      .attr('width', patternSize)
      .attr('height', patternSize)
      .append('image')
      .attr('href', centerImagePath)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', imageSize)
      .attr('height', imageSize);
  }

  // Create gradients for orbits with multiple colors
  // Use linear gradients with rotation animation for loader effect
  orbits.forEach((orbit, index) => {
    if (orbit.colors.length > 1) {
      // Create linear gradient centered on the SVG
      const gradient = defs
        .append('linearGradient')
        .attr('id', `orbitGradient${index}`)
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('x1', CENTER_X - orbit.radius)
        .attr('y1', CENTER_Y)
        .attr('x2', CENTER_X + orbit.radius)
        .attr('y2', CENTER_Y);

      // Add color stops with enhanced spacing for shimmer effect
      // Repeat colors to create circular gradient pattern
      const repeatedColors = [...orbit.colors, ...orbit.colors];

      repeatedColors.forEach((color, colorIndex) => {
        const offset = (colorIndex * 100) / (repeatedColors.length - 1);
        gradient
          .append('stop')
          .attr('offset', `${offset}%`)
          .attr('stop-color', color)
          .attr('stop-opacity', 0.7 + (colorIndex % 2) * 0.3); // Alternate opacity for shimmer
      });

      // Start rotation animation for this gradient
      startGradientRotation(gradient, orbit.speed, CENTER_X, CENTER_Y);
    }
  });

  // Create filled orbit circles (render in reverse order so smallest is on top)
  const orbitCircles = orbitGroup
    .selectAll<SVGCircleElement, OrbitConfig>('circle.orbit')
    .data(orbits.slice().reverse())
    .join('circle')
    .attr('class', 'orbit')
    .attr('cx', CENTER_X)
    .attr('cy', CENTER_Y)
    .attr('r', (d) => d.radius)
    .attr('fill', (d, i) => {
      // Use image pattern for 172-radius orbit if centerImagePath provided
      if (d.radius === 172 && centerImagePath) {
        return 'url(#background-image)';
      }
      const originalIndex = orbits.length - 1 - i;
      return d.colors.length > 1 ? `url(#orbitGradient${originalIndex})` : d.colors[0];
    })
    .attr('stroke', 'none')
    .attr('opacity', (_d, i) => {
      const originalIndex = orbits.length - 1 - i;
      // Outer rings fade into space (lower opacity), inner rings more solid
      // Enhanced depth: Range from 0.15 (outermost) to 1.0 (innermost)
      // Use exponential curve for more dramatic fade on outer rings
      const linearProgress = originalIndex / (orbits.length - 1);
      const exponentialProgress = Math.pow(linearProgress, 0.7);
      return 0.15 + exponentialProgress * 0.85;
    })
    .attr('filter', 'url(#ring-glow)')
    .style('cursor', onOrbitClick ? 'pointer' : 'default')
    .style('pointer-events', 'none');  // CRITICAL: Rings should NOT intercept clicks

  // Add click handlers if provided
  if (onOrbitClick) {
    orbitCircles.each(function (_d, i) {
      const originalIndex = orbits.length - 1 - i;
      d3.select(this).on('click', () => {
        onOrbitClick(originalIndex);
      });
    });
  }

  return orbitCircles;
}

export function createOrbitNodes(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  orbit: OrbitConfig,
  nodes: NodeData[],
  orbitIndex: number,
  onNodeClick?: (nodeId: string, nodeIndex: number, orbitIndex: number, label: string) => void,
  onNodeDoubleClick?: (nodeId: string, nodeIndex: number, orbitIndex: number, label: string) => void
): d3.Selection<SVGGElement, NodeData, SVGGElement, unknown> {
  const nodesGroup = svg.append('g').attr('class', `nodes-orbit-${orbitIndex}`);

  // Pre-compute size-aware initial angles to prevent node overlap at rest
  const initialAngles = calculateInitialSpacedAngles(nodes, orbit.radius, orbitIndex);

  const nodeGroups = nodesGroup
    .selectAll<SVGGElement, NodeData>('g.node')
    .data(nodes)
    .join('g')
    .attr('class', 'node')
    .attr('cursor', 'pointer')  // Always show pointer cursor - nodes are always clickable
    .style('pointer-events', 'all')  // CRITICAL: Ensure group catches pointer events
    .attr('filter', 'url(#node-glow)')
    .attr('transform', (_d, i) => {
      const angle = initialAngles.get(i) ?? (-Math.PI / 2);
      const x = CENTER_X + orbit.radius * Math.cos(angle);
      const y = CENTER_Y + orbit.radius * Math.sin(angle);
      return `translate(${x},${y})`;
    });

  // Get or create defs for gradients and clip paths
  const svgElement = nodeGroups.node()?.ownerSVGElement;
  const defs = (() => {
    if (!svgElement) return null;
    const svgSelection = d3.select(svgElement);
    if (svgSelection.select('defs').empty()) {
      return svgSelection.append('defs') as d3.Selection<SVGDefsElement, unknown, null, undefined>;
    }
    return svgSelection.select('defs') as d3.Selection<SVGDefsElement, unknown, null, undefined>;
  })();

  // Render nodes - use orbital style for sphere nodes, simple circle otherwise
  nodeGroups.each(function(d, nodeIndex) {
    const nodeGroup = d3.select(this as SVGGElement) as unknown as d3.Selection<SVGGElement, NodeData, SVGGElement, unknown>;

    if ((d.sphereData || d.sphereNode) && defs) {
      // Use orbital node renderer with status-colored ring
      renderOrbitalNode(nodeGroup, d, defs, orbitIndex, nodeIndex);
    } else if (d.imagePath) {
      // Simple image rendering for non-sphere nodes
      // Image goes FIRST (behind)
      // Lazy load images - store path in data-src, load via IntersectionObserver
      nodeGroup
        .append('image')
        .attr('class', 'lazy-image')
        .attr('data-src', d.imagePath)
        .attr('width', 50)
        .attr('height', 50)
        .attr('x', -25)
        .attr('y', -25)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .style('pointer-events', 'none');  // Don't block clicks

      // Click target goes LAST (on TOP) to receive all clicks
      nodeGroup
        .append('circle')
        .attr('class', 'click-target')
        .attr('r', 25)
        .attr('fill', 'rgba(0, 0, 0, 0.01)')
        .style('pointer-events', 'all')
        .style('cursor', 'pointer');
    } else {
      // Fallback: simple circle for nodes without images
      // Visual elements FIRST (behind)
      nodeGroup
        .append('circle')
        .attr('class', 'node-circle')
        .attr('r', NODE_RADIUS)
        .attr('fill', orbit.colors[0])
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 2)
        .attr('opacity', 0.9)
        .style('pointer-events', 'none');

      nodeGroup
        .append('text')
        .attr('class', 'node-label')
        .attr('text-anchor', 'middle')
        .attr('dy', '0.3em')
        .attr('fill', '#ffffff')
        .style('pointer-events', 'none')
        .attr('font-size', '12px')
        .attr('font-weight', 'bold')
        .text(d.label);

      // Click target LAST (on TOP) to receive all clicks
      nodeGroup
        .append('circle')
        .attr('class', 'click-target')
        .attr('r', NODE_RADIUS + 5)
        .attr('fill', 'rgba(0, 0, 0, 0.01)')
        .style('pointer-events', 'all')
        .style('cursor', 'pointer');
    }
  });

  // Add custom labels with gradient background
  nodeGroups.each(function(d, i) {
    if (d.customLabel) {
      const labelGroup = d3.select(this).append('g').attr('class', 'custom-label-group');

      // Measure text width to size the background appropriately
      const tempText = labelGroup
        .append('text')
        .attr('font-size', '14px')
        .attr('font-weight', 'bold')
        .attr('font-family', 'Arial, sans-serif')
        .text(d.customLabel);
      const textWidth = (tempText.node() as SVGTextElement)?.getBBox().width || 100;
      tempText.remove();

      const padding = 20;
      const rectWidth = textWidth + padding * 2;
      const rectHeight = 32;

      // Store label width as data attribute for collision detection
      labelGroup.attr('data-label-width', rectWidth);

      // Add rounded rectangle background with gradient
      labelGroup
        .append('rect')
        .attr('class', 'label-background')
        .attr('x', LABEL_OFFSET)
        .attr('y', -rectHeight / 2)
        .attr('width', rectWidth)
        .attr('height', rectHeight)
        .attr('rx', 20)
        .attr('ry', 20)
        .attr('fill', 'url(#labelGradient)')
        .attr('opacity', 0.75)
        .attr('stroke', 'rgba(255, 255, 255, 0.5)')
        .attr('stroke-width', 0.5)
        .attr('filter', 'url(#labelShadow)');

      // Add custom label text
      labelGroup
        .append('text')
        .attr('class', 'custom-label-text')
        .attr('x', LABEL_OFFSET + rectWidth / 2)
        .attr('y', 5)
        .attr('text-anchor', 'middle')
        .attr('fill', '#ffffff')
        .attr('font-size', '14px')
        .attr('font-weight', 'bold')
        .attr('font-family', 'Arial, sans-serif')
        .text(d.customLabel);

      // Set initial label position based on node's starting position
      const angle = initialAngles.get(i) ?? (-Math.PI / 2);
      const x = CENTER_X + orbit.radius * Math.cos(angle);
      updateLabelPosition(this as SVGGElement, x, rectWidth);
    }
  });

  // Add satellite circles based on satellites array (with status colors)
  nodeGroups.each(function(d) {
    const nodeGroup = d3.select(this as SVGGElement) as unknown as d3.Selection<SVGGElement, NodeData, SVGGElement, unknown>;
    const satellites = d.satellites || [];
    const satelliteCount = getSatelliteCount(d);

    if (satelliteCount > 0) {
      renderSatellites(nodeGroup, satellites, satelliteCount);
    }
  });

  // CRITICAL: Move click-targets to be absolutely LAST in each node group
  // This ensures they're on top of all other elements (labels, satellites, etc.)
  nodeGroups.each(function() {
    const nodeElement = this as SVGGElement;
    const clickTarget = d3.select(nodeElement).select('.click-target').node() as SVGCircleElement | null;

    if (clickTarget) {
      // Remove and re-append to make it the last child (on top)
      nodeElement.appendChild(clickTarget);
    }
  });

  // Add click handlers (backup - main selection happens on mousedown)
  nodeGroups.on('click', function (event, d) {
    const nodeIndex = nodes.indexOf(d);
    const label = d.customLabel || d.label;

    // Call the rotation handler if provided
    if (onNodeClick) {
      onNodeClick(d.id, nodeIndex, orbitIndex, label);
    }

    // Also call the node's own onClick if provided
    if (d.onClick) {
      d.onClick();
    }
  });

  // Helper function to handle hover enter on a node
  const handleNodeHoverEnter = (nodeElement: SVGGElement) => {
    // Pause ALL nodes in this orbit by interrupting their transitions
    const orbitGroup = nodeElement.parentNode as SVGGElement;
    const allNodesInOrbit = d3.select(orbitGroup).selectAll<SVGGElement, NodeData>('g.node');

    allNodesInOrbit.interrupt();

    // Add visual highlight to the hovered node
    d3.select(nodeElement).select('.node-circle, image')
      .transition()
      .duration(150)
      .attr('transform', 'scale(1.15)');
  };

  // Helper function to handle hover leave on a node
  const handleNodeHoverLeave = (nodeElement: SVGGElement) => {
    // Remove visual highlight
    d3.select(nodeElement).select('.node-circle, image')
      .transition()
      .duration(150)
      .attr('transform', 'scale(1)');

    // Don't restart spinning if orbit has settled into resting positions
    const orbitGroup = nodeElement.parentNode as SVGGElement;
    if (d3.select(orbitGroup).attr('data-settled') === 'true') {
      return;
    }

    // Resume animation for all nodes in the orbit
    const allNodesInOrbit = d3.select(orbitGroup).selectAll<SVGGElement, NodeData>('g.node');

    // Calculate average current angle to keep orbit synchronized
    let avgCurrentAngle = 0;
    let nodeCount = 0;
    allNodesInOrbit.each(function(_d, i) {
      const transform = d3.select(this).attr('transform');
      const match = transform?.match(/translate\(([-\d.]+),\s*([-\d.]+)\)/);
      if (match) {
        const x = Number.parseFloat(match[1]);
        const y = Number.parseFloat(match[2]);
        const dx = x - CENTER_X;
        const dy = y - CENTER_Y;
        const angle = Math.atan2(dy, dx);
        const nodeAngleOffset = (Math.PI * 2 * i) / nodes.length;
        avgCurrentAngle += angle - nodeAngleOffset;
        nodeCount++;
      }
    });

    if (nodeCount > 0) {
      avgCurrentAngle /= nodeCount;

      // Restart animation for all nodes together
      startOrbitAnimation(allNodesInOrbit, orbit, avgCurrentAngle);
    }
  };

  // IMPORTANT: Trigger node selection on MOUSEDOWN, not click
  // Because nodes are animating, by the time mouseup happens the node may have moved away
  // This makes clicking on moving nodes actually work
  nodeGroups.on('mousedown', function(event, d) {
    const nodeIndex = nodes.indexOf(d);
    const label = d.customLabel || d.label;

    // Trigger the node selection immediately on mousedown
    if (onNodeClick) {
      onNodeClick(d.id, nodeIndex, orbitIndex, label);
    }

    // Also call the node's own onClick if provided
    if (d.onClick) {
      d.onClick();
    }

    // Prevent the event from bubbling to avoid double-triggering via click handler
    event.stopPropagation();
  });

  // Double-click handler to center/navigate to the node
  nodeGroups.on('dblclick', function(event, d) {
    const nodeIndex = nodes.indexOf(d);
    const label = d.customLabel || d.label;

    // Call the double-click callback if provided
    if (onNodeDoubleClick) {
      onNodeDoubleClick(d.id, nodeIndex, orbitIndex, label);
    }

    event.stopPropagation();
  });

  // Add hover handlers to bring node to front
  nodeGroups
    .on('mouseenter', function () {
      handleNodeHoverEnter(this);
    })
    .on('mouseleave', function () {
      handleNodeHoverLeave(this);
    });

  // Add hover handlers to labels that trigger the same effect on parent node
  nodeGroups.each(function() {
    const nodeElement = this as SVGGElement;
    const labelGroup = d3.select(nodeElement).select('.custom-label-group');

    if (!labelGroup.empty()) {
      labelGroup
        .on('mouseenter', function(event) {
          event.stopPropagation(); // Prevent bubbling to node
          handleNodeHoverEnter(nodeElement);
        })
        .on('mouseleave', function(event) {
          event.stopPropagation(); // Prevent bubbling to node
          handleNodeHoverLeave(nodeElement);
        })
        .on('click', function(event) {
          // Allow label click to trigger node click
          event.stopPropagation();
          const d = d3.select(nodeElement).datum() as NodeData;
          const nodeIndex = nodes.indexOf(d);
          const label = d.customLabel || d.label;

          if (onNodeClick) {
            onNodeClick(d.id, nodeIndex, orbitIndex, label);
          }
          if (d.onClick) {
            d.onClick();
          }
        });
    }
  });

  return nodeGroups;
}

/**
 * Start continuous orbit animation for a group of nodes.
 *
 * @param nodeGroups - D3 selection of node groups to animate
 * @param orbit - Orbit configuration
 * @param initialAngle - Starting angle offset (default 0)
 * @param speedOverride - Optional speed override in ms (use for velocity-based animation)
 * @returns The D3 transition
 */
export function startOrbitAnimation(
  nodeGroups: d3.Selection<SVGGElement, NodeData, SVGGElement, unknown>,
  orbit: OrbitConfig,
  initialAngle = 0,
  speedOverride?: number
): d3.Transition<SVGGElement, NodeData, SVGGElement, unknown> {
  const animationSpeed = speedOverride ?? orbit.speed;

  return nodeGroups
    .transition()
    .duration(animationSpeed)
    .ease(d3.easeLinear)
    .attrTween('transform', function (d, i, nodes) {
      const nodeCount = nodes.length;
      const angleOffset = (Math.PI * 2 * i) / nodeCount;
      const nodeElement = this as SVGGElement;

      // Get label width for collision detection
      const labelGroup = d3.select(nodeElement).select('.custom-label-group');
      const labelWidth = labelGroup.empty() ? 0 : Number.parseFloat(labelGroup.attr('data-label-width') || '100');

      // Animate satellites if present
      const satelliteCount = getSatelliteCount(d);
      if (satelliteCount > 0) {
        const satelliteGroup = d3.select(this).select('.satellite-group') as d3.Selection<SVGGElement, unknown, null, undefined>;
        animateSatellites(satelliteGroup, satelliteCount, animationSpeed);
      }

      // Throttle transform updates to ~60fps max
      let lastUpdate = 0;
      let lastTransform = '';

      return function (t: number) {
        const now = Date.now();
        if (now - lastUpdate < RAF_THROTTLE_MS && lastTransform) {
          return lastTransform;
        }
        lastUpdate = now;

        const angle = initialAngle + angleOffset + t * Math.PI * 2;
        const x = CENTER_X + orbit.radius * Math.cos(angle);
        const y = CENTER_Y + orbit.radius * Math.sin(angle);

        // Update label position based on current x position
        if (labelWidth > 0) {
          updateLabelPosition(nodeElement, x, labelWidth);
        }

        lastTransform = `translate(${x},${y})`;
        return lastTransform;
      };
    })
    .on('end', function () {
      // Recursively restart animation for continuous rotation
      startOrbitAnimation(
        d3.select(this.parentNode as SVGGElement).selectAll<SVGGElement, NodeData>('g.node'),
        orbit,
        initialAngle + Math.PI * 2,
        speedOverride
      );
    });
}

/**
 * Start orbit animation with velocity-based speed calculation.
 * Uses the average velocity of nodes in the orbit to determine rotation speed.
 *
 * @param nodeGroups - D3 selection of node groups to animate
 * @param orbit - Orbit configuration
 * @param nodes - Array of NodeData to calculate average velocity from
 * @param initialAngle - Starting angle offset (default 0)
 * @returns The D3 transition
 */
export function startOrbitAnimationWithVelocity(
  nodeGroups: d3.Selection<SVGGElement, NodeData, SVGGElement, unknown>,
  orbit: OrbitConfig,
  nodes: NodeData[],
  initialAngle = 0
): d3.Transition<SVGGElement, NodeData, SVGGElement, unknown> {
  const avgVelocity = calculateAverageVelocity(nodes);
  const velocityBasedSpeed = calculateOrbitSpeed(orbit.speed, avgVelocity);
  return startOrbitAnimation(nodeGroups, orbit, initialAngle, velocityBasedSpeed);
}

/**
 * Start orbit animation from each node's current position instead of resetting to equal spacing.
 * Parses the current transform to get each node's angle, then spins one full revolution from there.
 */
export function startOrbitAnimationFromCurrent(
  nodeGroups: d3.Selection<SVGGElement, NodeData, SVGGElement, unknown>,
  orbit: OrbitConfig,
  speedOverride?: number
): d3.Transition<SVGGElement, NodeData, SVGGElement, unknown> {
  const animationSpeed = speedOverride ?? orbit.speed;

  // Parse each node's current angle from its transform
  const currentAngles: number[] = [];
  nodeGroups.each(function () {
    const transform = d3.select(this).attr('transform');
    const match = transform?.match(/translate\(([-\d.]+),\s*([-\d.]+)\)/);
    if (match) {
      currentAngles.push(Math.atan2(Number.parseFloat(match[2]) - CENTER_Y, Number.parseFloat(match[1]) - CENTER_X));
    } else {
      currentAngles.push(-Math.PI / 2);
    }
  });

  return nodeGroups
    .transition()
    .duration(animationSpeed)
    .ease(d3.easeLinear)
    .attrTween('transform', function (d, i) {
      const startAngle = currentAngles[i] ?? -Math.PI / 2;
      const nodeElement = this as SVGGElement;

      const labelGroup = d3.select(nodeElement).select('.custom-label-group');
      const labelWidth = labelGroup.empty() ? 0 : Number.parseFloat(labelGroup.attr('data-label-width') || '100');

      const satelliteCount = getSatelliteCount(d);
      if (satelliteCount > 0) {
        const satelliteGroup = d3.select(this).select('.satellite-group') as d3.Selection<SVGGElement, unknown, null, undefined>;
        animateSatellites(satelliteGroup, satelliteCount, animationSpeed);
      }

      // Throttle transform updates to ~60fps max
      let lastUpdate = 0;
      let lastTransform = '';

      return function (t: number) {
        const now = Date.now();
        if (now - lastUpdate < RAF_THROTTLE_MS && lastTransform) {
          return lastTransform;
        }
        lastUpdate = now;

        const angle = startAngle + t * Math.PI * 2;
        const x = CENTER_X + orbit.radius * Math.cos(angle);
        const y = CENTER_Y + orbit.radius * Math.sin(angle);

        if (labelWidth > 0) {
          updateLabelPosition(nodeElement, x, labelWidth);
        }

        lastTransform = `translate(${x},${y})`;
        return lastTransform;
      };
    })
    .on('end', function () {
      startOrbitAnimationFromCurrent(
        d3.select(this.parentNode as SVGGElement).selectAll<SVGGElement, NodeData>('g.node'),
        orbit,
        speedOverride
      );
    });
}

/**
 * Start orbit animation from current positions with velocity-based speed.
 */
export function startOrbitAnimationFromCurrentWithVelocity(
  nodeGroups: d3.Selection<SVGGElement, NodeData, SVGGElement, unknown>,
  orbit: OrbitConfig,
  nodes: NodeData[]
): d3.Transition<SVGGElement, NodeData, SVGGElement, unknown> {
  const avgVelocity = calculateAverageVelocity(nodes);
  const velocityBasedSpeed = calculateOrbitSpeed(orbit.speed, avgVelocity);
  return startOrbitAnimationFromCurrent(nodeGroups, orbit, velocityBasedSpeed);
}

export function animateSatellites(
  satelliteGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
  count: number,
  speed: number
) {
  const actualCount = Math.min(count, 4);

  for (let i = 0; i < actualCount; i++) {
    const satellite = satelliteGroup.select(`.satellite-${i}`);
    const startAngle = (i * Math.PI * 2) / actualCount;

    satellite
      .transition()
      .duration(speed)
      .ease(d3.easeLinear)
      .attrTween('transform', function() {
        // Throttle transform updates to ~60fps max
        let lastUpdate = 0;
        let lastTransform = '';

        return function(t: number) {
          const now = Date.now();
          if (now - lastUpdate < RAF_THROTTLE_MS && lastTransform) {
            return lastTransform;
          }
          lastUpdate = now;

          const angle = startAngle + t * Math.PI * 2;
          const x = 35 * Math.cos(angle);
          const y = 35 * Math.sin(angle);
          const currentX = Number.parseFloat(satellite.attr('cx'));
          const currentY = Number.parseFloat(satellite.attr('cy'));
          lastTransform = `translate(${x - currentX}, ${y - currentY})`;
          return lastTransform;
        };
      })
      .on('end', function() {
        // Recursively restart satellite animation
        animateSatellites(satelliteGroup, count, speed);
      });
  }
}

export function stopAnimations(svg: d3.Selection<SVGSVGElement, unknown, null, undefined>) {
  svg.selectAll('*').interrupt();
}

/**
 * Settle a single orbit: decelerate nodes from current spinning positions
 * to gravity-sorted resting positions with easeQuadOut easing.
 *
 * @param svg - Root SVG selection
 * @param orbitIndex - Index of the orbit to settle
 * @param orbit - Orbit configuration
 * @param nodes - Array of NodeData for this orbit
 * @param duration - Deceleration duration in ms
 * @returns Promise that resolves when all nodes in the orbit have settled
 */
export function settleOrbit(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  orbitIndex: number,
  orbit: OrbitConfig,
  nodes: NodeData[],
  duration: number
): Promise<void> {
  return new Promise((resolve) => {
    const orbitGroup = svg.select<SVGGElement>(`.nodes-orbit-${orbitIndex}`);
    const nodeGroups = orbitGroup.selectAll<SVGGElement, NodeData>('g.node');

    if (nodeGroups.empty() || nodes.length === 0) {
      resolve();
      return;
    }

    // Stop spinning transitions
    nodeGroups.interrupt();

    // Parse each node's current position from its transform attribute
    const currentAngles: number[] = [];
    nodeGroups.each(function () {
      const transform = d3.select(this).attr('transform');
      const match = transform?.match(/translate\(([-\d.]+),\s*([-\d.]+)\)/);
      if (match) {
        const x = Number.parseFloat(match[1]);
        const y = Number.parseFloat(match[2]);
        currentAngles.push(Math.atan2(y - CENTER_Y, x - CENTER_X));
      } else {
        // Fallback: place at 12 o'clock
        currentAngles.push(-Math.PI / 2);
      }
    });

    // Compute target angles based on gravity sorting with per-orbit stagger
    const targetAngles = calculateSettlementAngles(nodes, orbit.radius, orbitIndex);

    let completedCount = 0;
    const totalNodes = nodes.length;

    nodeGroups
      .transition()
      .duration(duration)
      .ease(d3.easeQuadOut)
      .attrTween('transform', function (_d, i) {
        const startAngle = currentAngles[i];
        const endAngle = targetAngles.get(i) ?? -Math.PI / 2;

        // Always settle clockwise (positive delta) to continue the spinning direction
        let delta = endAngle - startAngle;
        delta = ((delta % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

        const nodeElement = this as SVGGElement;
        const labelGroup = d3.select(nodeElement).select('.custom-label-group');
        const labelWidth = labelGroup.empty()
          ? 0
          : Number.parseFloat(labelGroup.attr('data-label-width') || '100');

        // Throttle transform updates to ~60fps max
        let lastUpdate = 0;
        let lastTransform = '';

        return function (t: number) {
          const now = Date.now();
          if (now - lastUpdate < RAF_THROTTLE_MS && lastTransform) {
            return lastTransform;
          }
          lastUpdate = now;

          const angle = startAngle + delta * t;
          const x = CENTER_X + orbit.radius * Math.cos(angle);
          const y = CENTER_Y + orbit.radius * Math.sin(angle);

          // Keep labels correctly positioned during deceleration
          if (labelWidth > 0) {
            updateLabelPosition(nodeElement, x, labelWidth);
          }

          lastTransform = `translate(${x},${y})`;
          return lastTransform;
        };
      })
      .on('end', function () {
        completedCount++;
        if (completedCount === totalNodes) {
          // Mark orbit group as settled so hover guard can check
          orbitGroup.attr('data-settled', 'true');
          resolve();
        }
      });
  });
}

/**
 * Settle all orbits with staggered timing so inner orbits settle first.
 * Skips orbits that the user has currently selected (they remain interactive).
 *
 * @param svg - Root SVG selection
 * @param orbits - Array of orbit configurations
 * @param allNodes - Array of arrays, one per orbit, containing the orbit's NodeData
 * @param staggerMs - Milliseconds between each orbit starting its settlement
 * @param skipOrbits - Optional set of orbit indices to skip (e.g. user-selected orbits)
 * @returns Promise that resolves when all non-skipped orbits have settled
 */
export function settleAllOrbits(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  orbits: OrbitConfig[],
  allNodes: NodeData[][],
  staggerMs: number,
  skipOrbits?: Set<number>
): Promise<void> {
  const promises: Promise<void>[] = [];

  for (let orbitIndex = 0; orbitIndex < orbits.length; orbitIndex++) {
    const nodes = allNodes[orbitIndex];
    if (!nodes || nodes.length === 0) continue;
    if (skipOrbits?.has(orbitIndex)) continue;

    const promise = new Promise<void>((resolve) => {
      setTimeout(() => {
        settleOrbit(svg, orbitIndex, orbits[orbitIndex], nodes, DECELERATION_DURATION)
          .then(resolve);
      }, orbitIndex * staggerMs);
    });

    promises.push(promise);
  }

  // If no orbits to settle, resolve immediately
  if (promises.length === 0) {
    return Promise.resolve();
  }

  return Promise.all(promises).then(() => undefined);
}

export function rotateOrbitToPosition(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  orbitIndex: number,
  nodeIndex: number,
  nodeCount: number,
  orbit: OrbitConfig,
  duration = 1000,
  targetAngle?: number
): Promise<void> {
  return new Promise((resolve) => {
    const nodeGroups = svg.selectAll<SVGGElement, NodeData>(`.nodes-orbit-${orbitIndex} g.node`);

    if (nodeGroups.empty()) {
      resolve();
      return;
    }

    // Stop any ongoing animations
    nodeGroups.interrupt();

    // Calculate the rotation needed to bring the selected node to the target position
    // Default target position is at angle -π/2 (12 o'clock)
    const target = targetAngle ?? -Math.PI / 2;
    const currentAngle = (Math.PI * 2 * nodeIndex) / nodeCount - Math.PI / 2;

    // Calculate shortest rotation direction
    let rotationOffset = target - currentAngle;

    // Normalize to [-π, π] range for shortest path
    while (rotationOffset > Math.PI) rotationOffset -= 2 * Math.PI;
    while (rotationOffset < -Math.PI) rotationOffset += 2 * Math.PI;

    // Animate all nodes in the orbit with smooth easing
    nodeGroups
      .transition()
      .duration(duration)
      .ease(d3.easeQuadInOut)
      .attrTween('transform', function (_d, i) {
        // Throttle transform updates to ~60fps max
        let lastUpdate = 0;
        let lastTransform = '';

        return function (t: number) {
          const now = Date.now();
          if (now - lastUpdate < RAF_THROTTLE_MS && lastTransform) {
            return lastTransform;
          }
          lastUpdate = now;

          const baseAngle = (Math.PI * 2 * i) / nodeCount - Math.PI / 2;
          const angle = baseAngle + rotationOffset * t;
          const x = CENTER_X + orbit.radius * Math.cos(angle);
          const y = CENTER_Y + orbit.radius * Math.sin(angle);
          lastTransform = `translate(${x},${y})`;
          return lastTransform;
        };
      })
      .on('end', function (_d, i) {
        // Only resolve once (when first node completes)
        if (i === 0) {
          resolve();
        }
      });
  });
}

export function getCurrentNodeAngles(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  orbitIndex: number
): number[] {
  const nodeGroups = svg.selectAll<SVGGElement, NodeData>(`.nodes-orbit-${orbitIndex} g.node`);
  const angles: number[] = [];

  nodeGroups.each(function () {
    const transform = d3.select(this).attr('transform');
    const match = transform.match(/translate\(([-\d.]+),\s*([-\d.]+)\)/);
    if (match) {
      const x = Number.parseFloat(match[1]) - CENTER_X;
      const y = Number.parseFloat(match[2]) - CENTER_Y;
      const angle = Math.atan2(y, x);
      angles.push(angle);
    }
  });

  return angles;
}